import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import Reader from './components/Reader';
import CommentsSection from './components/CommentsSection';
import { getComicById, comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

const CHAPTER_PREVIEW = 36;

function getCategoryName(id: string) {
  return categories.find((c) => c.id === id)?.name ?? '漫画';
}

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id ?? '');

  const [activeChapter, setActiveChapter] = useState<number | null>(null);
  const [sortAsc, setSortAsc] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [faved, setFaved] = useState(false);
  const readerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveChapter(null);
    setSortAsc(false);
    setShowAll(false);
    setFaved(false);
  }, [id]);

  useEffect(() => {
    if (activeChapter !== null && readerRef.current) {
      readerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeChapter]);

  if (!comic) {
    return (
      <>
        <SeoHead title="漫画不存在-57漫画网" description="未找到该漫画作品，返回57漫画网继续发现海量免费漫画。" canonicalPath="/category" />
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-emotion-sad-line h-14 w-14 text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-5 text-xl font-bold text-foreground-900 md:text-2xl">作品不存在或已下架</h1>
          <p className="mt-3 text-sm text-foreground-500">可能是链接有误，去分类页逛逛其他热门漫画吧</p>
          <Link to="/category" className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
            <i className="ri-arrow-left-line h-4 w-4" aria-hidden="true"></i>
            返回全部分类
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = comics
    .filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat)))
    .slice(0, 4);

  const totalChapters = comic.chapters;
  const chapterList = Array.from({ length: totalChapters }, (_, i) => (sortAsc ? i + 1 : totalChapters - i));
  const visibleChapters = showAll ? chapterList : chapterList.slice(0, CHAPTER_PREVIEW);

  const startReading = (chapter: number) => setActiveChapter(chapter);
  const goPrev = () => setActiveChapter((c) => (c === null ? null : Math.max(1, c - 1)));
  const goNext = () => setActiveChapter((c) => (c === null ? null : Math.min(totalChapters, c + 1)));

  const jsonLd = [
    {
      '@type': 'Article',
      '@id': `https://readfunny0.github.io/comic/${comic.id}#article`,
      headline: `《${comic.title}》漫画免费阅读-57漫画网`,
      description: comic.description,
      image: comic.cover,
      author: { '@type': 'Person', name: comic.author },
      datePublished: `${comic.year}-01-01`,
      dateModified: new Date().toISOString().slice(0, 10),
      mainEntityOfPage: `https://readfunny0.github.io/comic/${comic.id}`,
      publisher: { '@id': 'https://readfunny0.github.io/#organization' },
      inLanguage: 'zh-CN',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readfunny0.github.io/' },
        { '@type': 'ListItem', position: 2, name: '分类', item: 'https://readfunny0.github.io/category' },
        ...comic.categories.map((cat, i) => ({
          '@type': 'ListItem',
          position: i + 3,
          name: getCategoryName(cat),
          item: `https://readfunny0.github.io/category/${cat}`,
        })),
        { '@type': 'ListItem', position: comic.categories.length + 3, name: comic.title, item: `https://readfunny0.github.io/comic/${comic.id}` },
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title={`《${comic.title}》漫画-下拉式免费阅读-57漫画网`}
        description={`${comic.description} 57漫画网下拉式免费阅读《${comic.title}》，高清无删减，更新至${comic.latestChapter}。`}
        keywords={`${comic.title},${comic.tags.join(',')},免费阅读,57漫画网`}
        canonicalPath={`/comic/${comic.id}`}
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '分类', to: '/category' },
                ...comic.categories.map((cat) => ({ label: getCategoryName(cat), to: `/category/${cat}` })),
                { label: comic.title },
              ]}
            />
            <div className="mt-6 flex flex-col gap-6 md:mt-8 md:flex-row md:gap-8">
              <div className="mx-auto w-44 shrink-0 md:mx-0 md:w-56">
                <div className="relative overflow-hidden rounded-lg bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`《${comic.title}》漫画封面`}
                    title={`《${comic.title}》漫画免费阅读`}
                    className="aspect-[3/4] w-full object-cover object-top"
                  />
                  <span className="absolute left-2 top-2 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-background-50">
                    {comic.status}
                  </span>
                </div>
              </div>

              <div className="min-w-0 flex-1 text-center md:text-left">
                <h1 className="font-display text-2xl font-bold leading-snug text-foreground-900 md:text-4xl">
                  《{comic.title}》
                </h1>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 md:justify-start">
                  {comic.categories.map((cat) => (
                    <Link
                      key={cat}
                      to={`/category/${cat}`}
                      className="flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {getCategoryName(cat)}
                    </Link>
                  ))}
                  {comic.tags.map((tag) => (
                    <span key={tag} className="whitespace-nowrap rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">
                      #{tag}
                    </span>
                  ))}
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-4 text-center sm:grid-cols-4 md:text-left">
                  <div>
                    <dt className="text-[11px] text-foreground-500 md:text-xs">评分</dt>
                    <dd className="mt-0.5 flex items-center justify-center gap-1 text-sm font-bold text-secondary-600 md:justify-start md:text-base">
                      <i className="ri-star-fill h-4 w-4" aria-hidden="true"></i>
                      {comic.rating}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-foreground-500 md:text-xs">点击</dt>
                    <dd className="mt-0.5 text-sm font-bold text-foreground-900 md:text-base">{comic.views}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-foreground-500 md:text-xs">收藏</dt>
                    <dd className="mt-0.5 text-sm font-bold text-foreground-900 md:text-base">{comic.favorites}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-foreground-500 md:text-xs">章节</dt>
                    <dd className="mt-0.5 text-sm font-bold text-foreground-900 md:text-base">{comic.chapters} 话</dd>
                  </div>
                </dl>

                <p className="mt-4 text-xs leading-6 text-foreground-600 md:text-sm md:leading-7">{comic.description}</p>

                <p className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-foreground-500 md:justify-start">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line h-3.5 w-3.5" aria-hidden="true"></i>
                    作者：{comic.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line h-3.5 w-3.5" aria-hidden="true"></i>
                    {comic.year}年
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line h-3.5 w-3.5" aria-hidden="true"></i>
                    更新于 {comic.updatedAt}
                  </span>
                </p>

                <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center md:justify-start">
                  <button
                    type="button"
                    onClick={() => startReading(sortAsc ? 1 : totalChapters)}
                    className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-8 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto md:text-base"
                  >
                    <i className="ri-play-fill h-5 w-5" aria-hidden="true"></i>
                    开始阅读
                  </button>
                  <button
                    type="button"
                    onClick={() => setFaved((v) => !v)}
                    className={`flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-8 py-3 text-sm font-bold transition-colors sm:w-auto md:text-base ${
                      faved
                        ? 'border-secondary-400 bg-secondary-100 text-secondary-900'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    <i className={`${faved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} h-5 w-5`} aria-hidden="true"></i>
                    {faved ? '已收藏' : '加入书架'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={readerRef} className="scroll-mt-20">
          {activeChapter !== null && (
            <Reader
              comic={comic}
              chapter={activeChapter}
              total={totalChapters}
              onPrev={goPrev}
              onNext={goNext}
              onClose={() => setActiveChapter(null)}
            />
          )}
        </div>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12" aria-label="章节列表">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2.5 text-lg font-bold text-foreground-900 md:text-xl">
              <span className="h-5 w-1.5 rounded-full bg-primary-500" aria-hidden="true"></span>
              章节列表
              <span className="text-xs font-normal text-foreground-500">（共 {totalChapters} 话）</span>
            </h2>
            <button
              type="button"
              onClick={() => setSortAsc((v) => !v)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:text-sm"
            >
              <i className={`${sortAsc ? 'ri-sort-asc' : 'ri-sort-desc'} h-4 w-4`} aria-hidden="true"></i>
              {sortAsc ? '正序' : '倒序'}
            </button>
          </div>
          <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {visibleChapters.map((chapter) => (
              <li key={chapter}>
                <button
                  type="button"
                  onClick={() => startReading(chapter)}
                  className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-xs transition-colors md:text-sm ${
                    activeChapter === chapter
                      ? 'border-primary-500 bg-primary-50 font-bold text-primary-700'
                      : 'border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={`${activeChapter === chapter ? 'ri-bookmark-fill' : 'ri-book-open-line'} h-4 w-4 shrink-0 text-primary-500`} aria-hidden="true"></i>
                  <span className="truncate">第{chapter}话</span>
                </button>
              </li>
            ))}
          </ul>
          {!showAll && totalChapters > CHAPTER_PREVIEW && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-8 py-2.5 text-sm font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-down-double-line h-4 w-4" aria-hidden="true"></i>
                展开全部 {totalChapters} 话
              </button>
            </div>
          )}
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12">
          <CommentsSection />
        </section>

        {related.length > 0 && (
          <section className="border-t border-background-200 bg-background-100 py-10 md:py-14" aria-label="相关推荐">
            <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
              <h2 className="mb-5 flex items-center gap-2.5 text-lg font-bold text-foreground-900 md:mb-7 md:text-xl">
                <span className="h-5 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
                {related.map((item) => (
                  <ComicCard key={item.id} comic={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </>
  );
}